import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider as MaterialThemeProvider } from '@mui/material/styles';

type Mode = 'light' | 'dark';
const ThemeContext = createContext<{ mode: Mode; toggleTheme: () => void }>({ mode: 'light', toggleTheme: () => {} });

const initialMode = (): Mode => {
  try {
    const saved = localStorage.getItem('research-ethics-theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* Storage may be unavailable in private browsing. */ }
  return 'light';
};

export const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<Mode>(initialMode);
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try { localStorage.setItem('research-ethics-theme', mode); } catch { /* Keep the in-memory preference. */ }
  }, [mode]);
  const context = useMemo(() => ({ mode, toggleTheme: () => setMode(current => current === 'dark' ? 'light' : 'dark') }), [mode]);
  const theme = useMemo(() => createTheme({
    palette: { mode, primary: { main: '#2453b3' }, background: { default: mode === 'dark' ? '#061426' : '#f4f8fd', paper: mode === 'dark' ? '#0d2340' : '#ffffff' } },
    typography: { fontFamily: 'Inter, "Segoe UI", sans-serif', fontSize: 14 },
    shape: { borderRadius: 10 },
  }), [mode]);
  return <ThemeContext.Provider value={context}><MaterialThemeProvider theme={theme}>{children}</MaterialThemeProvider></ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
